import { OrderAmendments } from "@/components/dealer/OrderAmendments";
import { RefundStatus } from "@/components/orders/RefundStatus";
import { dealerLabel } from "@/lib/naming";
import { DiscontinuedBadge } from "@/components/catalog/DiscontinuedBadge";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { NavigationFailure, reasonFromError } from "@/components/NavigationFailure";
import { useCustomerView } from "@/context/CustomerViewContext";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CreditCard, Landmark, X, Printer, Copy } from "lucide-react";
import { ORDER_STATUS_BADGE } from "./DealerOrders";
import { ORDER_STATUS, LINE_STATUS, ORDER_EVENT, human } from "@/lib/statusLabels";

function HoldCountdown({ expiresAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return <span className="text-xs text-status-sold">Hold expired</span>;
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return <span className="font-mono text-xs text-status-sold" data-testid="checkout-countdown">{m}m {s}s left to complete checkout</span>;
}

export default function DealerOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { customerView } = useCustomerView();
  const [navFail, setNavFail] = useState(null);
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);
  const [dcDialog, setDcDialog] = useState(null); // "cancel" | "request"
  const [dcReason, setDcReason] = useState("");
  const [dcSaving, setDcSaving] = useState(false);
  const [terms, setTerms] = useState(null);
  const [ack, setAck] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteAck, setQuoteAck] = useState(false);
  const notified = useRef(false);
  const isSupply = !!order?.direct && order?.fulfillment_mode !== "FULL_CONTAINER";
  const payable = order && !customerView && ["CHECKOUT_HOLD", "AWAITING_DEPOSIT", "CONFIRMED", "BALANCE_DUE"].includes(order.status);

  useEffect(() => {
    if (!payable) return;
    api.get(`/orders/${id}/terms`).then((r) => { setTerms(r.data); if (r.data.acknowledged_at) setAck(true); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, order?.status, customerView]);

  const openQuote = async (mode) => {
    try {
      const { data } = await api.get(`/orders/${id}/cancellation-quote`);
      setQuote(data); setQuoteAck(false); setDcDialog(mode);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const load = () =>
    api
      .get(`/orders/${id}${customerView ? "?customer_view=1" : ""}`)
      .then((res) => setOrder(res.data))
      .catch((e) => setNavFail(reasonFromError(e)));  // NAV-009

  useEffect(() => {
    setOrder(null); // clear stale payload before refetch — CV and trade payloads have different fields
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, customerView]);

  useEffect(() => {
    if (!order || !["CHECKOUT_HOLD", "PAYMENT_PROCESSING", "AWAITING_DEPOSIT", "BALANCE_DUE"].includes(order.status)) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.status]);

  useEffect(() => {
    if (notified.current) return;
    if (params.get("checkout") === "success") {
      toast.info("Checkout returned — confirming payment status…");
      notified.current = true;
    } else if (params.get("checkout") === "cancel") {
      toast.info("Checkout cancelled — your hold remains until it expires.");
      notified.current = true;
    }
  }, [params]);

  const handlePay = async (method) => {
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${id}/checkout`, { method, origin_url: window.location.origin, terms_acknowledged: ack });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPaying(false);
    }
  };

  const handleCancel = async () => {
    try {
      await api.post(`/orders/${id}/cancel`, {});
      toast.success("Order cancelled — inventory released");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDcPay = async (phase, method = "card") => {
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${id}/${phase}-checkout`, { origin_url: window.location.origin, method, terms_acknowledged: ack });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPaying(false);
    }
  };

  const submitDcDialog = async () => {
    setDcSaving(true);
    try {
      if (dcDialog === "cancel" || dcDialog === "quote") {
        const { data } = await api.post(`/orders/${id}/cancel`, { acknowledge_quote: quoteAck });
        if (data.refund_result) toast.success(`Order cancelled — ${data.refund_result.message}`);
        else if (data.status === "CANCELLED") toast.success("Order cancelled — inventory released");
        else if (data.cancellation_request?.status === "PENDING" || data.direct?.cancellation_request?.status === "PENDING") toast.success("Cancellation request sent to Zelligio for approval");
        else toast.info("Cancellation sent to Zelligio for review — the refund will be confirmed manually");
      } else {
        await api.post(`/orders/${id}/cancellation-request`, { reason: dcReason });
        toast.success("Cancellation request sent to Zelligio for review");
      }
      setDcDialog(null);
      setDcReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDcSaving(false);
    }
  };

  if (navFail) return <NavigationFailure reason={navFail} moduleLabel="Orders" modulePath="/dealer/orders" />;
  if (!order) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="order-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  return (
    <div data-testid="dealer-order-detail">
      <Button variant="ghost" onClick={() => navigate("/dealer/orders")} data-testid="back-to-orders" className="mb-6 -ml-3 gap-2 text-warmgrey hover:text-charcoal">
        <ArrowLeft className="h-4 w-4" /> Orders
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-light tracking-tight text-charcoal" data-testid="order-number">{order.order_number || "Order"}</h1>
          <p className="mt-1 text-xs text-muted-foreground">{new Date(order.created_at).toLocaleString()}</p>
          {!customerView && <OrderAmendments order={order} onDone={load} />}
        </div>
        <div className="flex items-center gap-3">
          {order.fulfillment_mode === "FULL_CONTAINER" && (
            <span className="badge-status badge-available" data-testid="container-mode-badge">Full Container</span>
          )}
          <span className={`badge-status ${ORDER_STATUS_BADGE[order.status] || "badge-neutral"}`} data-testid="order-status">{human(ORDER_STATUS, order.status)}</span>
        </div>
      </div>

      {payable && terms && (
        <Card className="mt-6 border-border bg-white" data-testid="terms-disclosure">
          <CardContent className="p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Payment & cancellation terms</p>
            <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2">
              <div className="flex justify-between gap-3"><dt className="text-warmgrey">Order total</dt><dd className="num text-charcoal" data-testid="terms-total">${terms.order_total_usd.toFixed(2)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-warmgrey">{terms.deposit_pct ? "Required deposit (30%)" : "Full payment required"}</dt><dd className="num text-charcoal" data-testid="terms-due-now">${terms.due_now_usd.toFixed(2)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-warmgrey">Remaining balance</dt><dd className="num text-charcoal" data-testid="terms-remaining">${terms.remaining_balance_usd.toFixed(2)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-warmgrey">{order.status === "CHECKOUT_HOLD" ? "Payment deadline" : "Reservation expires"}</dt><dd className="text-charcoal" data-testid="terms-expires">{terms.reservation_expires_at ? new Date(terms.reservation_expires_at).toLocaleString() : "—"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-warmgrey">Paid-order grace period</dt><dd className="text-right text-charcoal" data-testid="terms-grace">{terms.paid_order_grace_period}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-warmgrey">Cancellation charge</dt><dd className="text-right text-charcoal" data-testid="terms-charge">{terms.cancellation_charge}</dd></div>
              <div className="flex justify-between gap-3 sm:col-span-2"><dt className="text-warmgrey">Final sale</dt><dd className="text-right text-charcoal" data-testid="terms-final-sale">{terms.final_sale_point}</dd></div>
            </dl>
            <p className="mt-3 text-xs leading-relaxed text-warmgrey" data-testid="terms-policy">{terms.policy_text}</p>
            <p className="mt-2 text-xs leading-relaxed text-warmgrey" data-testid="terms-fees">{terms.processing_fees}</p>
            <label className="mt-4 flex items-start gap-2 text-xs text-charcoal">
              <input type="checkbox" checked={ack} disabled={!!terms.acknowledged_at} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 accent-[#B86F54]" data-testid="terms-ack-checkbox" />
              <span>I have read and accept the payment, reservation-expiration, cancellation and processing-fee terms above.{terms.acknowledged_at ? ` (accepted ${new Date(terms.acknowledged_at).toLocaleString()})` : ""}</span>
            </label>
          </CardContent>
        </Card>
      )}

      {!customerView && <RefundStatus order={order} />}

      {order.status === "CHECKOUT_HOLD" && !customerView && (
        <Card className="mt-6 border-terracotta/40 bg-white" data-testid="checkout-panel">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
            <div>
              <p className="text-sm font-medium text-charcoal">Complete payment to secure this inventory</p>
              <HoldCountdown expiresAt={order.checkout_expires_at} />
              <p className="mt-1 text-xs text-warmgrey" data-testid="checkout-release-warning">
                Pay by {new Date(order.checkout_expires_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} — if payment is not completed, the boxes are released back to available stock.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => handlePay("card")} disabled={paying || !ack} data-testid="pay-card-button" className="bg-terracotta text-white hover:bg-terracotta-hover">
                <CreditCard className="mr-2 h-4 w-4" /> Pay by card
              </Button>
              <Button onClick={() => handlePay("ach")} disabled={paying || !ack} variant="outline" data-testid="pay-ach-button" className="border-terracotta text-terracotta hover:bg-[#BC6955]/5">
                <Landmark className="mr-2 h-4 w-4" /> Pay by bank (ACH)
              </Button>
              <Button onClick={handleCancel} variant="ghost" data-testid="cancel-order-button" className="text-status-sold hover:bg-sand">
                <X className="mr-1 h-4 w-4" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {order.direct && !customerView && order.status === "AWAITING_DEPOSIT" && (
        <Card className="mt-6 border-terracotta/40 bg-white" data-testid="dc-deposit-panel">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
            <div>
              <p className="text-sm font-medium text-charcoal">30% deposit due to {isSupply ? "commit this pre-reservation" : "confirm this container order"}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Deposit ${order.direct.deposit_amount_usd.toFixed(2)} now · the 70% balance (${order.direct.balance_amount_usd.toFixed(2)}) is due {isSupply ? "before Florida dispatch or pickup" : "only when Zelligio marks the order ready to ship"}.
                {isSupply && order.payment_deadline_at && <span data-testid="deposit-deadline"> Pay by {new Date(order.payment_deadline_at).toLocaleString()} or the allocation is released.</span>}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => handleDcPay("deposit", "card")} disabled={paying || !ack} data-testid="dc-pay-deposit-button" className="bg-terracotta text-white hover:bg-terracotta-hover">
                <CreditCard className="mr-2 h-4 w-4" /> Pay 30% deposit
              </Button>
              {isSupply && (
                <Button onClick={() => handleDcPay("deposit", "ach")} disabled={paying || !ack} variant="outline" data-testid="dc-pay-deposit-ach-button" className="border-terracotta text-terracotta">
                  <Landmark className="mr-2 h-4 w-4" /> Deposit by bank (ACH)
                </Button>
              )}
              <Button onClick={() => setDcDialog("cancel")} variant="ghost" data-testid="dc-cancel-predeposit-button" className="text-status-sold hover:bg-sand">
                <X className="mr-1 h-4 w-4" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isSupply && !customerView && (order.status === "CONFIRMED" || order.status === "BALANCE_DUE") && (
        <Card className="mt-6 border-border bg-white" data-testid="supply-status-panel">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
            <div>
              <p className="text-sm font-medium text-charcoal">Committed — deposit received</p>
              <p className="mt-1 text-xs text-muted-foreground" data-testid="supply-balance-note">
                Balance ${order.direct.balance_amount_usd.toFixed(2)} due before Florida dispatch or pickup · deposit ${order.direct.deposit_amount_usd.toFixed(2)} paid {order.direct.deposit?.paid_at ? new Date(order.direct.deposit.paid_at).toLocaleDateString() : ""}.
                {order.status === "BALANCE_DUE" && " Zelligio has marked this order ready — please settle the balance."}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={() => handleDcPay("balance", "card")} disabled={paying || !ack} data-testid="supply-pay-balance-button" className="bg-terracotta text-white hover:bg-terracotta-hover">
                <CreditCard className="mr-2 h-4 w-4" /> Pay 70% balance
              </Button>
              {order.direct.cancellation_request?.status === "PENDING" ? (
                <span className="badge-status badge-reserved" data-testid="dc-request-pending">Cancellation under review</span>
              ) : (
                <Button onClick={() => openQuote("quote")} variant="ghost" data-testid="supply-cancel-button" className="text-status-sold hover:bg-sand">
                  <X className="mr-1 h-4 w-4" /> Cancel order
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!isSupply && order.direct && !customerView && (order.status === "CONFIRMED" || order.status === "BALANCE_DUE") && (
        <Card className="mt-6 border-border bg-white" data-testid="dc-status-panel">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
            <div>
              {order.status === "BALANCE_DUE" ? (
                <>
                  <p className="text-sm font-medium text-charcoal">Ready to ship — 70% balance due</p>
                  <p className="mt-1 text-xs text-muted-foreground">Balance ${order.direct.balance_amount_usd.toFixed(2)} · deposit of ${order.direct.deposit_amount_usd.toFixed(2)} confirmed {order.direct.confirmed_at ? `on ${new Date(order.direct.confirmed_at).toLocaleDateString()}` : ""}.</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-charcoal">Confirmed — deposit received</p>
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="dc-window-note">
                    {Date.now() < new Date(order.direct.cancel_self_serve_until).getTime()
                      ? `Free cancellation with full refund until ${new Date(order.direct.cancel_self_serve_until).toLocaleString()}.`
                      : Date.now() < new Date(order.direct.cancel_admin_window_until).getTime()
                        ? "Self-service cancellation has closed — you can request a cancellation for Zelligio review."
                        : "This order is commercially committed."}
                  </p>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              {order.status === "BALANCE_DUE" && (
                <Button onClick={() => handleDcPay("balance")} disabled={paying} data-testid="dc-pay-balance-button" className="bg-terracotta text-white hover:bg-terracotta-hover">
                  <CreditCard className="mr-2 h-4 w-4" /> Pay 70% balance
                </Button>
              )}
              {order.direct.cancellation_request?.status === "PENDING" ? (
                <span className="badge-status badge-reserved" data-testid="dc-request-pending">Cancellation requested — under review</span>
              ) : Date.now() < new Date(order.direct.cancel_self_serve_until).getTime() ? (
                <Button onClick={() => setDcDialog("cancel")} variant="ghost" data-testid="dc-cancel-button" className="text-status-sold hover:bg-sand">
                  <X className="mr-1 h-4 w-4" /> Cancel — full refund
                </Button>
              ) : Date.now() < new Date(order.direct.cancel_admin_window_until).getTime() ? (
                <Button onClick={() => setDcDialog("request")} variant="ghost" data-testid="dc-request-cancel-button" className="text-status-sold hover:bg-sand">
                  <X className="mr-1 h-4 w-4" /> Request cancellation
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      )}

      {order.status === "PAID" && (
        <Card className="mt-6 border-border bg-white" data-testid="order-next-step">
          <CardContent className="p-5">
            {!customerView && !order.lines.some((l) => l.fulfillment_status === "FULFILLED") && (
              <div className="mb-3 flex justify-end">
                {(order.direct?.cancellation_request?.status === "PENDING" || order.cancellation_request?.status === "PENDING") ? (
                  <span className="badge-status badge-reserved" data-testid="paid-request-pending">Cancellation under review</span>
                ) : (
                  <Button onClick={() => openQuote("quote")} variant="ghost" size="sm" data-testid="paid-cancel-button" className="text-status-sold hover:bg-sand">
                    <X className="mr-1 h-4 w-4" /> Cancel before dispatch
                  </Button>
                )}
              </div>
            )}
            {order.direct && !customerView && (
              <p className="mb-2 text-xs text-muted-foreground" data-testid="dc-payment-breakdown">
                Deposit paid ${order.direct.deposit_amount_usd.toFixed(2)} · Balance paid ${order.direct.balance_amount_usd.toFixed(2)}
              </p>
            )}
            {order.lines.some((l) => l.fulfillment_status === "AWAITING_RECEIPT") ? (
              <>
                <p className="text-sm font-medium text-charcoal" data-testid="next-step-waiting">
                  Paid — waiting for {order.lines.find((l) => l.fulfillment_status === "AWAITING_RECEIPT")?.container_display || "incoming inventory"}
                  {order.lines.find((l) => l.fulfillment_status === "AWAITING_RECEIPT")?.container_eta ? ` · ETA ${order.lines.find((l) => l.fulfillment_status === "AWAITING_RECEIPT").container_eta}` : ""}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">No action required — we'll prepare your order as soon as the container arrives.</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-charcoal">Paid — your order is being prepared for shipment.</p>
                <p className="mt-1 text-xs text-muted-foreground">No action required.</p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {order.status === "PAYMENT_PROCESSING" && (
        <Card className="mt-6 border-border bg-white" data-testid="ach-processing-panel">
          <CardContent className="p-6">
            <p className="text-sm text-charcoal">ACH bank debit is settling — inventory remains committed. The order is marked paid only after settlement confirms.</p>
            {order.ach_deadline_at && <p className="mt-1 text-xs text-muted-foreground">Settlement deadline: {new Date(order.ach_deadline_at).toLocaleDateString()}</p>}
          </CardContent>
        </Card>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border bg-white lg:col-span-2">
          <CardContent className="p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Lines</p>
            <div className="mt-4 space-y-3">
              {order.lines.map((l) => (
                <div key={l.line_id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background p-4" data-testid={`order-line-${l.line_id}`}>
                  <div>
                    <p className="text-sm font-medium text-charcoal">{dealerLabel(l)}<DiscontinuedBadge variantId={l.variant_id} /></p>
                    <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                      {l.sku} · {l.boxes} boxes · {l.sqft} sqft ·{" "}
                      {l.source === "FLORIDA" ? "Florida" : `incoming · ${l.container_display || "container en route"}`}
                    </p>
                    {l.shipped_tracking && !(order.shipments || []).some((s) => s.line_ids.includes(l.line_id)) && (
                      <p className="mt-0.5 font-mono text-xs text-charcoal" data-testid={`line-legacy-tracking-${l.line_id}`}>{l.shipped_carrier} {l.shipped_tracking}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {!customerView && l.pricing_label && (
                      <span className="badge-status badge-available" data-testid={`line-pricing-label-${l.line_id}`}>{l.pricing_label}</span>
                    )}
                    {!customerView && l.line_subtotal != null && <span className="text-sm text-charcoal" data-testid={`line-subtotal-${l.line_id}`}>${l.line_subtotal.toFixed(2)}</span>}
                    {customerView && l.retail_line_total != null && <span className="text-sm text-charcoal">${l.retail_line_total.toFixed(2)}</span>}
                    <span className="badge-status badge-neutral" data-testid={`line-fulfillment-${l.line_id}`}>{human(LINE_STATUS, l.fulfillment_status)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end border-t border-border pt-4">
              <div className="text-right text-sm text-muted-foreground" data-testid="order-totals-block">
                {order.freight && !customerView && (
                  <>
                    <p>Product subtotal <span className="ml-3 text-charcoal" data-testid="order-subtotal">${order.totals.subtotal_usd.toFixed(2)}</span></p>
                    <p className="mt-1">
                      Freight & logistics {order.freight.status === "ESTIMATE" ? "(estimate)" : ""}
                      <span className="ml-3 text-charcoal" data-testid="order-freight">${order.freight.amount_usd.toFixed(2)}</span>
                    </p>
                  </>
                )}
                <p className="mt-1">
                  {order.freight && !customerView ? "Estimated total " : "Total "}
                  <span className="num text-xl font-semibold text-charcoal" data-testid="order-total">
                    ${(customerView ? order.totals.retail_total : (order.totals.grand_total_usd ?? order.totals.total_usd))?.toFixed(2) ?? "—"}
                  </span>
                  {order.totals.tax_status === "UNRESOLVED" && !customerView && <span className="ml-2 text-xs">(tax calculated at checkout)</span>}
                  {order.totals.tax_status === "EXEMPT" && !customerView && (
                    <span className="ml-2 text-xs text-status-available" data-testid="order-tax-exempt">tax exempt · resale certificate{order.tax?.certificate_state ? ` (${order.tax.certificate_state})` : ""}</span>
                  )}
                  {(order.totals.tax_status === "COLLECTED" || order.totals.tax_status === "NOT_COLLECTED") && !customerView && (
                    <span className="ml-2 text-xs" data-testid="order-tax">sales tax ${(order.totals.tax_amount ?? 0).toFixed(2)}</span>
                  )}
                  {order.payment?.captured_usd != null && !customerView && (
                    <span className="ml-2 text-xs font-medium text-charcoal" data-testid="order-paid-total">· total charged ${order.payment.captured_usd.toFixed(2)}</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-border bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Ship to</p>
              <p className="mt-3 text-sm font-medium text-charcoal" data-testid="ship-to-name">{order.ship_to.recipient_name}</p>
              {order.ship_to.company && <p className="text-xs text-muted-foreground">{order.ship_to.company}</p>}
              <p className="mt-1 text-xs text-muted-foreground" data-testid="ship-to-address">
                {order.ship_to.address?.line1}{order.ship_to.address?.line2 ? `, ${order.ship_to.address.line2}` : ""}, {order.ship_to.address?.city}
                {order.ship_to.address?.state ? `, ${order.ship_to.address.state}` : ""} {order.ship_to.address?.postal_code}
              </p>
              {order.ship_to.delivery_instructions && <p className="mt-2 text-xs italic text-warmgrey">{order.ship_to.delivery_instructions}</p>}
            </CardContent>
          </Card>

          {order.lines.some((l) => l.fulfillment_status === "FULFILLED") && (
            <Button variant="outline" onClick={() => navigate(`/dealer/orders/${id}/packing-slip`)} data-testid="packing-slip-button" className="w-full border-limestone text-charcoal hover:bg-sand">
              <Printer className="mr-2 h-4 w-4" /> Packing slip
            </Button>
          )}

          {order.shipments?.length > 0 && (
            <Card className="border-border bg-white" data-testid="order-shipments">
              <CardContent className="p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Shipments</p>
                <div className="mt-3 space-y-4">
                  {order.shipments.map((s, i) => (
                    <div key={s.id} className="rounded-md border border-border bg-background p-4" data-testid={`shipment-${i}`}>
                      <p className="text-xs font-semibold uppercase tracking-wider text-terracotta">Shipment {i + 1}{s.shipment_type === "freight" ? " · Freight" : " · Parcel"}</p>
                      <p className="mt-1 text-sm font-medium text-charcoal">{s.carrier}{s.service ? ` ${s.service}` : ""}</p>
                      {s.tracking_number && (
                        <p className="mt-0.5 font-mono text-xs text-charcoal">
                          {s.shipment_type === "freight" ? "PRO" : "Tracking"}: {s.tracking_number}
                          {s.pieces != null ? ` · ${s.pieces} ${s.shipment_type === "freight" ? "pallet(s)" : "package(s)"}` : ""}
                        </p>
                      )}
                      <p className="mt-1 text-xs font-medium text-charcoal" data-testid={`shipment-status-${i}`}>{s.status_label}{s.delivered_at ? ` · ${new Date(s.delivered_at).toLocaleDateString()}` : ""}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {s.tracking_status === "PACKED" ? "Packed" : "Shipped"} {new Date(s.shipped_at).toLocaleDateString()}
                        {s.estimated_delivery ? ` · Estimated delivery ${new Date(s.estimated_delivery + "T00:00:00").toLocaleDateString()}` : ""}
                      </p>
                      {s.tracking_url ? (
                        <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`track-shipment-${i}`} className="mt-1 inline-block text-xs font-semibold text-terracotta hover:text-terracotta-hover">Track shipment →</a>
                      ) : s.tracking_number ? (
                        <button type="button" onClick={() => { navigator.clipboard?.writeText(s.tracking_number); toast.success("Carrier reference copied"); }} data-testid={`copy-tracking-${i}`} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-charcoal hover:text-terracotta">
                          <Copy className="h-3 w-3" /> Copy {s.shipment_type === "freight" ? "PRO" : "tracking"} reference · online tracking not available for this carrier
                        </button>
                      ) : null}
                      {order.ship_to?.kind === "direct_to_client" && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/shipments/${s.id}/client-slip.pdf`} target="_blank" rel="noopener noreferrer" data-testid={`client-slip-${i}`} className="mt-1 ml-3 inline-block text-xs font-semibold text-charcoal hover:text-terracotta">Client packing slip (PDF) →</a>
                      )}
                      <p className="mt-2 text-xs text-muted-foreground">Contents: {s.line_ids.map((lid) => order.lines.find((l) => l.line_id === lid)).filter(Boolean).map(dealerLabel).join(", ")}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-border bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Timeline</p>
              <div className="mt-3 space-y-2" data-testid="order-events">
                {order.events.map((e, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    <span className="font-medium text-charcoal">{human(ORDER_EVENT, e.status)}</span> · {new Date(e.at).toLocaleString()}
                    {e.note ? ` — ${e.note}` : ""}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Dialog open={!!dcDialog} onOpenChange={(o) => !o && setDcDialog(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">
              {dcDialog === "quote" ? "Cancel this order?" : dcDialog === "cancel" ? (isSupply ? "Cancel this pre-reservation order?" : "Cancel this container order?") : "Request cancellation"}
            </DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              {dcDialog === "quote"
                ? "Review the refund calculation before confirming. Processing fees are the actual non-recoverable fees charged by the payment processor — never estimated."
                : dcDialog === "cancel"
                ? (isSupply ? "No deposit has been paid — the allocation is released with nothing to refund." : "You are inside the 72-hour window — the deposit is refunded in full to the original payment method and the factory allocation is released.")
                : "The 72-hour self-service window has closed. Zelligio will review your request — the deposit is non-refundable unless an exception is approved."}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-4">
            {dcDialog === "quote" && quote && (
              <div className="rounded-md border border-border bg-background p-4 text-xs" data-testid="cancellation-quote">
                <div className="flex justify-between"><span className="text-warmgrey">Amount paid</span><span className="num text-charcoal" data-testid="quote-paid">${quote.paid_total_usd.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-warmgrey">Non-recoverable processing fees</span><span className="num text-charcoal" data-testid="quote-fees">{quote.fee_unknown ? "Pending review" : `− $${quote.fees_total_usd.toFixed(2)}`}</span></div>
                <div className="flex justify-between"><span className="text-warmgrey">Cancellation charge ({Math.round(quote.charge_pct * 100)}% of order value)</span><span className="num text-charcoal" data-testid="quote-charge">− ${quote.charge_usd.toFixed(2)}</span></div>
                <div className="mt-2 flex justify-between border-t border-border pt-2 text-sm font-semibold"><span className="text-charcoal">Refund</span><span className="num text-charcoal" data-testid="quote-refund">{quote.requires_admin_review ? "Zelligio review" : `$${quote.refund_usd.toFixed(2)}`}</span></div>
                <ul className="mt-2 list-disc space-y-0.5 pl-4 text-warmgrey" data-testid="quote-reasons">{quote.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                {quote.cancellable && (
                  <label className="mt-3 flex items-start gap-2 text-charcoal">
                    <input type="checkbox" checked={quoteAck} onChange={(e) => setQuoteAck(e.target.checked)} className="mt-0.5 accent-[#B86F54]" data-testid="quote-ack-checkbox" />
                    <span>I understand the refund calculation above and want to cancel this order.</span>
                  </label>
                )}
              </div>
            )}
            {dcDialog === "request" && (
              <Input value={dcReason} onChange={(e) => setDcReason(e.target.value)} placeholder="Reason (optional)" data-testid="dc-request-reason" className="h-11 bg-white focus-visible:ring-terracotta" />
            )}
            <div className="flex gap-2">
              <Button onClick={submitDcDialog} disabled={dcSaving || (dcDialog === "quote" && (!quote?.cancellable || !quoteAck))} data-testid="dc-dialog-confirm" className="flex-1 bg-status-sold text-white hover:opacity-90">
                {dcSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : dcDialog === "quote" ? (quote?.requires_admin_review ? "Send for review" : "Confirm cancellation") : dcDialog === "cancel" ? (isSupply ? "Cancel order" : "Cancel order — refund deposit") : "Send request"}
              </Button>
              <Button variant="outline" onClick={() => setDcDialog(null)} data-testid="dc-dialog-dismiss" className="flex-1">Keep order</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
